import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/apiClient";
import type { BouquetBuildSummary } from "../../api/types";
import { bouquetAge } from "../../utils/bouquetAge";
import { NavIcon } from "../nav/NavIcon";
import {
  bouquetDisplayName,
  bouquetLinesSummary,
  groupBouquetsByShop,
  isHeld,
  matchesBouquetSearch,
  sortReadyBouquets,
} from "../../utils/readyBouquets";

/**
 * Bouquet pictures, fetched once per picture and shared: two cards built from
 * one template show one download, and closing and reopening the view within a
 * minute reuses what is already here instead of fetching every card again.
 * An unused picture's blob URL is revoked a minute after its last card goes.
 */
const IMAGE_KEEP_MS = 60_000;
const imageCache = new Map<
  string,
  { promise: Promise<string | null>; refs: number; timer: number | null }
>();

function acquireImage(key: string, load: () => Promise<string | null>): Promise<string | null> {
  let entry = imageCache.get(key);
  if (!entry) {
    entry = { promise: load().catch(() => null), refs: 0, timer: null };
    imageCache.set(key, entry);
  }
  if (entry.timer !== null) {
    window.clearTimeout(entry.timer);
    entry.timer = null;
  }
  entry.refs += 1;
  return entry.promise;
}

function releaseImage(key: string): void {
  const entry = imageCache.get(key);
  if (!entry) return;
  entry.refs -= 1;
  if (entry.refs > 0) return;
  entry.timer = window.setTimeout(() => {
    imageCache.delete(key);
    void entry.promise.then((url) => {
      if (url) URL.revokeObjectURL(url);
    });
  }, IMAGE_KEEP_MS);
}

/**
 * The photo to show: the build's first photo, else its template's image, else
 * nothing (the card draws a neutral placeholder).
 */
function useBouquetImage(b: BouquetBuildSummary): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const photoId = b.photoId ?? null;
  const templateId = b.template?.imageKey ? b.template.id : null;
  useEffect(() => {
    const key = photoId ? `photo:${b.id}:${photoId}` : templateId ? `template:${templateId}` : null;
    if (!key) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    void acquireImage(key, () =>
      photoId
        ? api.fetchBouquetBuildPhotoBlobUrl(b.id, photoId)
        : api.fetchBouquetTemplateImageBlobUrl(templateId!),
    ).then((next) => {
      if (!cancelled) setUrl(next);
    });
    return () => {
      cancelled = true;
      releaseImage(key);
    };
  }, [b.id, photoId, templateId]);
  return url;
}

function BouquetCard({ bouquet }: { bouquet: BouquetBuildSummary }) {
  const image = useBouquetImage(bouquet);
  const held = isHeld(bouquet);
  const age = bouquetAge(bouquet.createdAt);
  const lines = bouquetLinesSummary(bouquet);
  const price = bouquet.suggestedSalePriceEur;

  return (
    <li className={held ? "inv-bq-card is-held" : "inv-bq-card"}>
      <div className="inv-bq-photo">
        {image ? <img src={image} alt="" /> : <span className="inv-bq-photo-empty" aria-hidden="true">
            <NavIcon name="flower" size={32} />
          </span>}
      </div>
      <div className="inv-bq-body">
        <div className="inv-bq-name">{bouquetDisplayName(bouquet)}</div>
        <div className="inv-bq-price">
          {price != null ? (
            <>
              €{Number(price).toFixed(2)} <span className="inv-bq-price-note">suggested</span>
            </>
          ) : (
            <span className="inv-bq-price-note">no suggested price</span>
          )}
        </div>
        {lines.text ? (
          <div className="inv-bq-lines">
            {lines.text}
            {lines.more > 0 ? `, +${lines.more} more` : ""}
          </div>
        ) : null}
        <div className={`inv-bq-age is-${age.tone}`}>{age.label}</div>
        <div className="inv-bq-by">by {bouquet.builtBy.fullName}</div>
        {held ? <div className="inv-bq-held">Held for order {bouquet.forOrder!.code}</div> : null}
      </div>
    </li>
  );
}

function BouquetGrid({ bouquets }: { bouquets: BouquetBuildSummary[] }) {
  return (
    <ul className="inv-bq-grid">
      {bouquets.map((b) => (
        <BouquetCard key={b.id} bouquet={b} />
      ))}
    </ul>
  );
}

/**
 * G-132 — bouquets built and waiting, as a view of the inventory page.
 *
 * Read-only, as the card it replaces was: they are sold from the Sales page.
 * Held bouquets (spoken for by an order) sort last, greyed, and are not
 * counted. An owner looking at every shop sees them grouped by shop.
 */
export function ReadyBouquetsView({
  bouquets,
  query,
  groupByShop,
  shops,
  error,
}: {
  /** null while loading. */
  bouquets: BouquetBuildSummary[] | null;
  query: string;
  groupByShop: boolean;
  shops: { id: string; name: string }[];
  error: string | null;
}) {
  const visible = useMemo(
    () => (bouquets ?? []).filter((b) => matchesBouquetSearch(b, query)),
    [bouquets, query],
  );

  const groups = useMemo(
    () => (groupByShop ? groupBouquetsByShop(visible, shops) : []),
    [groupByShop, visible, shops],
  );

  if (error) return <div className="alert error">{error}</div>;
  if (bouquets === null) return <p className="muted inv-empty">Loading ready bouquets…</p>;

  if (groupByShop) {
    return (
      <div className="inv-bq-groups">
        {groups.map((g) => (
          <section key={g.shopId} className="inv-bq-group" aria-label={g.shopName}>
            <h3 className="inv-group-head">
              {g.shopName} · {g.readyCount > 0 ? g.readyCount : "none ready"}
            </h3>
            {g.bouquets.length > 0 ? <BouquetGrid bouquets={g.bouquets} /> : null}
          </section>
        ))}
      </div>
    );
  }

  if (visible.length === 0) {
    return (
      <p className="muted inv-empty">
        {query.trim() ? "No ready bouquet matches your search." : "No bouquets are ready at the moment."}
      </p>
    );
  }
  return <BouquetGrid bouquets={sortReadyBouquets(visible)} />;
}
